/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { axios, dataFetcher, endpoints, baseUrl } from 'Api';
import { Pathname } from 'Routes';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Radio
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  Close,
  CloudUpload,
  PhotoLibrary,
  Public,
  Lock,
  Group,
  CheckCircle,
  Delete,
  Add,
  AddCircleOutline,
  FolderOpen,
  ImageOutlined,
  PlaylistPlay
} from '@mui/icons-material';
import {
  saveUploadInfo
} from 'store/store';
import useSWR from 'swr';
import { AppContext } from 'Context';
import { ALLOWED_UPLOADS_COUNT, MAX_PHOTOS_PER_UPLOAD } from 'Commons';
import uploadManager from 'Utils/uploadManagerSecure';
import { createThumbnails, revokeThumbnails, revokeThumbnail } from 'Utils/createThumbnail';
import { logAppEvent } from '../../Api/errorLogger';
import styles from './PhotoUploadModal.module.css';

const PhotoUploadModal = ({ open, onClose, userData = null }) => {
  const {
    navigateToInviteFirstTime,
    setAppSnackbar,
    pendingPhotoFiles,
    setPendingPhotoFiles,
    subscriptionDetails,
    freePlanLimitMb,
    totalOnGoingUploadSize,
    IsfreePlan,
    userDetails,
    autoSignIn,
    userAccessedData,
  } = useContext(AppContext);
  const fileInputRef = useRef(null);
  const reorderScrollRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  // Photo states
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState([]);
  // Downsized JPEG blob URLs for grid rendering (avoids decoding full-res images and
  // crashing Safari on large batches). photoUrls remains the source of truth for upload.
  const [previewUrls, setPreviewUrls] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('2');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedThumbnailIndex, setSelectedThumbnailIndex] = useState(0);
  const [isPublishing, setIsPublishing] = useState(false);
  const getToken = () => localStorage.getItem('token');
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const dispatch = useDispatch();

  // New states for album tabs
  const [albumMode, setAlbumMode] = useState(0); // 0 = Create Album, 1 = Existing Album
  const [existingAlbums, setExistingAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [loadingAlbums, setLoadingAlbums] = useState(false);
  const [reorderModalOpen, setReorderModalOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [currentLimitError, setCurrentLimitError] = useState(null);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  // { attempted, added } when a pick overshot MAX_PHOTOS_PER_UPLOAD — drives the cap popup.
  const [photoCapNotice, setPhotoCapNotice] = useState(null);
  const uploadPercentages = useSelector((state) => state.upload);
  const currentlyUploadingCount = uploadManager.getCurrentlyUploadingCount();
  const sameToAllowed = currentlyUploadingCount + userData?.totalContentUploaded + selectedPhotos.length === ALLOWED_UPLOADS_COUNT;
  const updateUploadPercentages = (draft_id, data) => {
    dispatch(saveUploadInfo({ [draft_id]: data }));
  };
  // Function to check and update upload limit error based on current photo count
  const checkUploadLimit = useCallback((currentPhotoCount, updateErrorState = true) => {
    // Check if user has data and no subscription
    if (userData && userData.subscription == null) {
      // Get currently uploading/pending items from upload manager
      const uploading = uploadManager.getCurrentlyUploadingCount();
      const totalAfterCurrent = (userData.totalContentUploaded || 0)
        + uploading
        + currentPhotoCount;
      if (totalAfterCurrent > ALLOWED_UPLOADS_COUNT) {
        const errorData = {
          currentCount: userData.totalContentUploaded || 0,
          tryingToUpload: currentPhotoCount,
          maxAllowed: ALLOWED_UPLOADS_COUNT,
          message: `You can upload a maximum of ${ALLOWED_UPLOADS_COUNT} files on free plan. ${userData.totalContentUploaded === 0
            ? `You are trying to upload ${currentPhotoCount}.`
            : `You can upload ${ALLOWED_UPLOADS_COUNT - (userData.totalContentUploaded + uploading)
            } more files`
            }`,
          messagex: `Upload limit exceeded. You can upload a maximum of ${ALLOWED_UPLOADS_COUNT} items. You currently have ${userData.totalContentUploaded || 0} items and are trying to upload ${currentPhotoCount} more.`
        };

        if (updateErrorState) setCurrentLimitError(errorData);
        return errorData;
      }
      if (freePlanLimitMb && selectedPhotos.length > 0) {
        const totalSizeMB = selectedPhotos.reduce((sum, photo) => sum + (photo.size / 1024 / 1024), 0);
        const usedStorage = subscriptionDetails?.storageUsed?.total_storage;
        const allFullStorage = totalSizeMB + usedStorage + totalOnGoingUploadSize;
        const exceededBy = allFullStorage - freePlanLimitMb;
        console.log('Checking storage limits:', { allFullStorage, freePlanLimitMb, usedStorage, totalSizeMB, totalOnGoingUploadSize });
        if (IsfreePlan && allFullStorage > freePlanLimitMb) {
          const errorData = {
            totalSize: allFullStorage.toFixed(2),
            maxAllowed: freePlanLimitMb,
            message: `File size limit exceeded. Free plan users can upload up to ${freePlanLimitMb} MB total. Your selected photos${selectedPhotos.length > 1 ? 's' : ''} total ${totalSizeMB.toFixed(2)} MB — you exceeded the limit by ${exceededBy.toFixed(2)} MB.`,
          };
          if (updateErrorState) setCurrentLimitError(errorData);
          return errorData;
        }
      }
    }

    // No error - clear any existing error
    if (updateErrorState) setCurrentLimitError(null);
    return null;
  }, [userData]);

  // Check upload limit when photos are selected
  useEffect(() => {
    if (selectedPhotos.length > 0 && userData) {
      checkUploadLimit(selectedPhotos.length);
    } else if (selectedPhotos.length === 0) {
      setCurrentLimitError(null);
    }
  }, [selectedPhotos.length, checkUploadLimit, userData]);

  // Fetch groups
  const { isValidating: gettingGroups, mutate: getGroups } = useSWR([endpoints.getMyGroups], {
    fetcher: (url) => dataFetcher(url),
    onSuccess: ({ success, data, message }) => {
      if (success && data) {
        setGroups(data);
        if (data.length > 0 && visibility === 'Group' && selectedGroups.length === 0) {
          setSelectedGroups([data[0].id]);
        }
      } else if (!success && message === 'No Groups found') {
        setGroups([]);
      }
      setGroupsLoaded(true);
    },
    onError: (error) => {
      console.error('Groups API error:', error);
      setGroups([]);
      setGroupsLoaded(true);
    }
  });

  // Fetch existing albums
  const fetchExistingAlbums = async () => {
    setLoadingAlbums(true);
    try {
      const response = await axios.post(`${baseUrl}/getAlbumList`, {
        token: getToken()
      });

      if (response.data && response.data.data) {
        setExistingAlbums(response.data.data);
      } else {
        setExistingAlbums([]);
      }
    } catch (error) {
      console.error('Error fetching albums:', error);
      setExistingAlbums([]);
    } finally {
      setLoadingAlbums(false);
    }
  };

  // Trigger initial data fetching when modal opens
  useEffect(() => {
    if (open) {
      setGroupsLoaded(false);
      getGroups();
      if (selectedPhotos.length > 0) {
        fetchExistingAlbums();
      }
    }
  }, [open, getGroups]);

  // Trim an incoming pick down to the per-upload photo cap, counting whatever
  // is already selected. Overshooting raises the cap popup rather than failing
  // silently — the leftovers can go into the same album in a later upload.
  const takeWithinPhotoCap = (incomingFiles) => {
    const remainingSlots = Math.max(MAX_PHOTOS_PER_UPLOAD - selectedPhotos.length, 0);
    if (incomingFiles.length <= remainingSlots) return incomingFiles;
    setPhotoCapNotice({ attempted: incomingFiles.length, added: remainingSlots });
    return incomingFiles.slice(0, remainingSlots);
  };

  // "Add More" — block at the cap with the popup instead of opening a picker
  // whose whole selection would be thrown away.
  const handleAddMorePhotosClick = () => {
    if (selectedPhotos.length >= MAX_PHOTOS_PER_UPLOAD) {
      setPhotoCapNotice({ attempted: 0, added: 0 });
      return;
    }
    fileInputRef.current?.click();
  };

  // Cap-popup copy: the rule, what happened to this pick, and the way out —
  // one paragraph, since every line renders at the same size and colour.
  const photoCapMessage = (() => {
    if (!photoCapNotice) return '';
    const rule = `You can't choose more than ${MAX_PHOTOS_PER_UPLOAD} photos in a single upload`;
    const skipped = photoCapNotice.attempted - photoCapNotice.added;
    if (!photoCapNotice.added) {
      return `${rule}, and this one already has ${MAX_PHOTOS_PER_UPLOAD}. You can add more photos to the same album once this upload finishes.`;
    }
    return `${rule}, so we kept the first ${photoCapNotice.added} of the ${photoCapNotice.attempted} you picked. You can add the other ${skipped} to the same album once this upload finishes.`;
  })();

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const allowedImageTypes = ['.jpeg', '.jpg', '.png', '.webp', '.heic', '.heif'];
    const validImageFiles = files.filter((file) => {
      const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      return allowedImageTypes.includes(fileExtension);
    });

    if (validImageFiles.length === 0) {
      console.error('No valid image files selected. Supported formats: JPEG, JPG, PNG, WebP, HEIC, HEIF');
      return;
    }

    const imageFiles = takeWithinPhotoCap(validImageFiles);
    if (imageFiles.length === 0) return;

    const wasEmpty = selectedPhotos.length === 0;
    const newPhotos = [...selectedPhotos, ...imageFiles];
    const newUrls = [...photoUrls];
    const startIndex = photoUrls.length;

    imageFiles.forEach((file) => {
      const url = URL.createObjectURL(file);
      newUrls.push(url);
    });

    setSelectedPhotos(newPhotos);
    setPhotoUrls(newUrls);
    // Reserve preview slots so indexes stay aligned with photoUrls.
    setPreviewUrls((prev) => [...prev, ...new Array(imageFiles.length).fill(null)]);

    // Generate downsized thumbnails in the background, bounded concurrency.
    // photoUrls (used by the upload pipeline) is untouched.
    createThumbnails(imageFiles, { maxDim: 320, quality: 0.7, concurrency: 3 })
      .then((thumbs) => {
        setPreviewUrls((prev) => {
          const next = [...prev];
          thumbs.forEach((thumbUrl, i) => {
            const slot = startIndex + i;
            // If the slot was removed/reordered, drop the thumb.
            if (slot < next.length) next[slot] = thumbUrl;
            else revokeThumbnail(thumbUrl);
          });
          return next;
        });
      })
      .catch(() => { /* fallback to original blob URL is fine */ });

    // Set default title if none
    if (!title && newPhotos.length > 0) {
      const filename = newPhotos[0].name.replace(/\.[^/.]+$/, '');
      setTitle(filename);
    }

    // Fetch albums when first photo is selected
    if (wasEmpty && newPhotos.length > 0) {
      fetchExistingAlbums();
    }
  };

  // Process pending photo files from global state
  useEffect(() => {
    if (open && pendingPhotoFiles && pendingPhotoFiles.length > 0 && selectedPhotos.length === 0) {
      // Create a synthetic event to pass to handleFileSelect
      const syntheticEvent = {
        target: {
          files: pendingPhotoFiles
        }
      };
      handleFileSelect(syntheticEvent);

      // Clear pending photo files from global state after processing
      setPendingPhotoFiles(null);
    }
  }, [open, pendingPhotoFiles, selectedPhotos.length]);

  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    const allowedImageTypes = ['.jpeg', '.jpg', '.png', '.webp', '.heic', '.heif'];

    const validImageFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      if (allowedImageTypes.includes(fileExtension)) {
        validImageFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    // Invalid files are silently ignored

    if (validImageFiles.length === 0) return;

    const imageFiles = takeWithinPhotoCap(validImageFiles);
    if (imageFiles.length === 0) return;

    const wasEmpty = selectedPhotos.length === 0;
    const newPhotos = [...selectedPhotos, ...imageFiles];
    const newUrls = [...photoUrls];
    const startIndex = photoUrls.length;

    imageFiles.forEach((file) => {
      const url = URL.createObjectURL(file);
      newUrls.push(url);
    });

    setSelectedPhotos(newPhotos);
    setPhotoUrls(newUrls);
    setPreviewUrls((prev) => [...prev, ...new Array(imageFiles.length).fill(null)]);

    createThumbnails(imageFiles, { maxDim: 320, quality: 0.7, concurrency: 3 })
      .then((thumbs) => {
        setPreviewUrls((prev) => {
          const next = [...prev];
          thumbs.forEach((thumbUrl, i) => {
            const slot = startIndex + i;
            if (slot < next.length) next[slot] = thumbUrl;
            else revokeThumbnail(thumbUrl);
          });
          return next;
        });
      })
      .catch(() => { /* fallback to original blob URL is fine */ });

    if (!title && newPhotos.length > 0) {
      const filename = newPhotos[0].name.replace(/\.[^/.]+$/, '');
      setTitle(filename);
    }

    // Fetch albums when first photo is selected
    if (wasEmpty && newPhotos.length > 0) {
      fetchExistingAlbums();
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const removePhoto = (index) => {
    const newPhotos = selectedPhotos.filter((_, i) => i !== index);
    const newUrls = photoUrls.filter((_, i) => i !== index);
    const newPreviews = previewUrls.filter((_, i) => i !== index);

    // Clean up URL
    URL.revokeObjectURL(photoUrls[index]);
    revokeThumbnail(previewUrls[index]);

    setSelectedPhotos(newPhotos);
    setPhotoUrls(newUrls);
    setPreviewUrls(newPreviews);

    // Adjust selected thumbnail if necessary
    if (selectedThumbnailIndex >= newPhotos.length) {
      setSelectedThumbnailIndex(Math.max(0, newPhotos.length - 1));
    }
  };

  const handleClose = () => {
    // Don't clean up photoUrls here since we need them for background upload.
    // Preview URLs are display-only — safe to revoke immediately to free memory.
    revokeThumbnails(previewUrls);
    // Reset state
    setSelectedPhotos([]);
    setPhotoUrls([]);
    setPreviewUrls([]);
    setTitle('');
    setDescription('');
    setVisibility('2');
    setSelectedGroups([]);
    setSelectedThumbnailIndex(0);
    setIsPublishing(false);
    setAlbumMode(0);
    setSelectedAlbum(null);
    setExistingAlbums([]);

    onClose();
  };

  // Reorder functions
  const handleReorderModalOpen = () => {
    setReorderModalOpen(true);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleReorderDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const container = reorderScrollRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const scrollThreshold = 80;
    const scrollSpeed = 15;
    const mouseY = e.clientY;

    const distFromTop = mouseY - containerRect.top;
    const distFromBottom = containerRect.bottom - mouseY;

    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }

    if (distFromTop < scrollThreshold && distFromTop >= 0 && container.scrollTop > 0) {
      scrollIntervalRef.current = setInterval(() => {
        if (container.scrollTop > 0) {
          container.scrollTop -= scrollSpeed;
        } else {
          clearInterval(scrollIntervalRef.current);
          scrollIntervalRef.current = null;
        }
      }, 16);
    } else if (distFromBottom < scrollThreshold && distFromBottom >= 0) {
      const maxScroll = container.scrollHeight - container.clientHeight;
      scrollIntervalRef.current = setInterval(() => {
        if (container.scrollTop < maxScroll) {
          container.scrollTop += scrollSpeed;
        } else {
          clearInterval(scrollIntervalRef.current);
          scrollIntervalRef.current = null;
        }
      }, 16);
    }
  };

  const handleReorderDragEnter = (e) => {
    e.preventDefault();
  };

  const handleReorderDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      return;
    }

    const newPhotos = [...selectedPhotos];
    const newUrls = [...photoUrls];
    const newPreviews = [...previewUrls];

    // Remove dragged items
    const draggedPhoto = newPhotos.splice(draggedIndex, 1)[0];
    const draggedUrl = newUrls.splice(draggedIndex, 1)[0];
    const draggedPreview = newPreviews.splice(draggedIndex, 1)[0];

    // Insert at new position
    newPhotos.splice(dropIndex, 0, draggedPhoto);
    newUrls.splice(dropIndex, 0, draggedUrl);
    newPreviews.splice(dropIndex, 0, draggedPreview);

    // Update the main state immediately
    setSelectedPhotos(newPhotos);
    setPhotoUrls(newUrls);
    setPreviewUrls(newPreviews);

    // Update selected thumbnail index if needed
    const oldThumbnailPhoto = selectedPhotos[selectedThumbnailIndex];
    const newThumbnailIndex = newPhotos.findIndex((photo) => (
      photo.name === oldThumbnailPhoto.name && photo.lastModified === oldThumbnailPhoto.lastModified
    ));
    if (newThumbnailIndex !== -1) {
      setSelectedThumbnailIndex(newThumbnailIndex);
    }

    setDraggedIndex(null);
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedIndex(null);
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };
  // Convert File/Blob URL to Base64
  const blobUrlToBase64 = async (blobUrl) => {
    const response = await fetch(blobUrl);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result); // base64 string
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Function to upload photos in background with batches
  const uploadPhotosInBackground = async (albumId, photosData, albumTitle, isNewAlbum = true, preUploadCount = 0) => {
    try {
      const { urls, fileNames, thumbnailIndex, visibilityValue, groupIds } = photosData;
      const totalPhotos = urls.length;
      let successfulUploadCount = 0;

      // Register photo upload with upload manager
      uploadManager.createPhotoUpload({
        albumId,
        albumTitle: albumTitle || 'Photo Album',
        photoCount: totalPhotos,
        updateUploadPercentages,
      });

      // Initialize upload percentage
      updateUploadPercentages(albumId, `0/${totalPhotos}`);

      // Upload photos one at a time for better progress tracking (1/3, 2/3, 3/3)
      const batchSize = 1;
      const batches = [];

      for (let i = 0; i < urls.length; i += batchSize) {
        batches.push(urls.slice(i, i + batchSize));
      }

      // Get icon base64 once - only for new albums
      const iconBase64 = isNewAlbum ? await blobUrlToBase64(urls[thumbnailIndex]) : null;

      // Process all batches - prepare them first, then execute
      const batchPromises = batches.map(async (batch, batchIndex) => {
        console.log(`Processing batch ${batchIndex + 1} of ${batches.length} (${batch.length} photos)`);

        try {
          const formData = new FormData();
          formData.append('album_id', albumId);
          formData.append('visibility', visibilityValue);
          formData.append('group_id', (groupIds || []).join(','));
          // Only append icon for new albums to preserve existing album thumbnails
          if (isNewAlbum && iconBase64) {
            formData.append('icon', iconBase64);
          }
          formData.append('token', getToken());

          // Convert batch URLs to blobs
          const photoPromises = batch.map(async (url, index) => {
            const response = await fetch(url);
            const blob = await response.blob();
            // Calculate the original index to get the correct file name
            const originalIndex = batchIndex * batchSize + index;
            return { blob, originalIndex };
          });

          const photoBlobs = await Promise.all(photoPromises);

          // Append batch blobs to formData with original file names
          photoBlobs.forEach(({ blob, originalIndex }) => {
            const fileName = fileNames?.[originalIndex] || `photo_${originalIndex}.jpg`;
            formData.append('photo_file[]', blob, fileName);
          });

          // Return the prepared formData for this batch
          return { formData, batchIndex, photoCount: batch.length };
        } catch (error) {
          console.error(`Error preparing batch ${batchIndex + 1}:`, error);
          return null;
        }
      });

      // Prepare all batches
      const preparedBatches = await Promise.all(batchPromises);

      // Now upload each batch sequentially using reduce
      await preparedBatches.reduce(async (previousPromise, batchData) => {
        await previousPromise;

        if (batchData) {
          try {
            await axios.post(`${baseUrl}/addPhotoContent`, batchData.formData);
            console.log(`Batch ${batchData.batchIndex + 1} uploaded successfully`);

            // Update successful upload count and percentage
            successfulUploadCount += batchData.photoCount;
            updateUploadPercentages(
              albumId,
              `${successfulUploadCount}/${totalPhotos}`
            );

            // Update upload manager progress
            uploadManager.updatePhotoUploadProgress(
              albumId,
              successfulUploadCount,
              totalPhotos
            );
          } catch (error) {
            console.error(`Error uploading batch ${batchData.batchIndex + 1}:`, error);
            // Continue with next batch even if this one fails
          }
        }
      }, Promise.resolve());

      // Clear the upload percentage after completion
      updateUploadPercentages(albumId, null);

      // Complete upload in upload manager
      uploadManager.completePhotoUpload(albumId);

      // Clean up URLs after all uploads complete
      urls.forEach((url) => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });

      console.log('All photo uploads completed');

      // Navigate to ecosystem for first-time users after upload completes
      await autoSignIn();
      navigateToInviteFirstTime(preUploadCount);
    } catch (error) {
      console.error('Error in background photo upload:', error);
      // Clear the upload percentage on error
      updateUploadPercentages(albumId, null);

      logAppEvent({
        userName: `${userDetails?.firstname || localStorage.getItem('firstname') || ''} ${userDetails?.lastname || localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
        functionAffected: 'Upload',
        description: `Photo upload failed — album: "${albumId}", reason: ${error?.message || 'Unknown error'}`,
        severity: 'high',
        errorMessage: error?.message || 'Photo upload failed',
        errorStack: error?.stack || null,
        component: 'PhotoUploadModal',
        userAction: 'Photo upload called',
      });

      // Fail upload in upload manager
      uploadManager.failPhotoUpload(albumId, error.message || 'Upload failed');
    }
  };

  const addEditAlbum = async () => {
    try {
      const response = await axios.post(`${baseUrl}/addEditAlbum`, {
        title,
        description,
        visibility,
        group_id: visibility === '3' ? selectedGroups.join(',') : '',
        token: getToken(),
      });

      const albumId = response.data?.album_id;

      if (albumId) {
        // Capture count BEFORE upload starts
        const preUploadCount = userDetails?.totalContentUploaded || 0;

        // Store data needed for background upload before closing modal
        const photosData = {
          urls: [...photoUrls], // Create copy of arrays
          fileNames: selectedPhotos.map((photo) => photo.name), // Preserve original file names
          thumbnailIndex: selectedThumbnailIndex,
          visibilityValue: visibility,
          groupIds: [...selectedGroups]
        };

        // Close modal immediately
        handleClose();
        navigate(`${Pathname.yourVideos}?tab=photoalbum`);

        // Start background upload process for new album
        uploadPhotosInBackground(albumId, photosData, title, true, preUploadCount);
      }

      return response.data;
    } catch (error) {
      console.error('Error in addEditAlbum:', error);
      setIsPublishing(false);
      throw error;
    }
  };
  const handlePublish = async () => {
    if (selectedPhotos.length === 0) return;

    if (albumMode === 0) {
      // Create Album mode
      // Validate group selection if visibility is Group
      if (visibility === '3' && (!selectedGroups || selectedGroups.length === 0)) {
        alert('Please select at least one group when Group visibility is selected.');
        return;
      }
      setIsPublishing(true);
      addEditAlbum();
    } else {
      // Existing Album mode
      if (!selectedAlbum) {
        alert('Please select an album to add photos to.');
        return;
      }
      setIsPublishing(true);

      // Capture count BEFORE upload starts
      const preUploadCount = userDetails?.totalContentUploaded || 0;

      // Store data needed for background upload before closing modal
      const photosData = {
        urls: [...photoUrls], // Create copy of arrays
        fileNames: selectedPhotos.map((photo) => photo.name), // Preserve original file names
        thumbnailIndex: selectedThumbnailIndex,
        visibilityValue: selectedAlbum.visibility || '2',
        groupIds: selectedAlbum.group_id ? [selectedAlbum.group_id] : []
      };

      // Close modal immediately
      handleClose();
      navigate(`${Pathname.yourVideos}?tab=photoalbum`);

      // Start background upload process for existing album (don't change thumbnail)
      uploadPhotosInBackground(selectedAlbum.id, photosData, selectedAlbum.title, false, preUploadCount);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 20,
            maxWidth: 750,
            maxHeight: '90vh',
            backgroundImage: 'none',
            boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 24px 64px rgba(15,23,42,0.18)',
          }
        }}
        BackdropProps={{
          style: {
            backgroundColor: 'rgba(15,17,28,0.45)',
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
          },
        }}
      >
        <DialogContent style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Header — new-UI theme: ink-tinted icon tile, title + step hint, and
              a real close button (there was none, so the only way out was the
              backdrop). */}
          <div className={styles.header}>
            <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <span className={styles.headIcon}>
                <PhotoLibrary />
              </span>
              <div style={{ minWidth: 0 }}>
                <h2 className={styles.titleText}>
                  {selectedPhotos.length > 0 ? 'Upload Photo Details' : 'Select Photos to Upload'}
                </h2>
                <div className={styles.titleHint}>
                  {selectedPhotos.length > 0
                    ? 'Choose an album, a cover photo and who can see it'
                    : 'Pick one or more photos from your device to get started'}
                </div>
              </div>
            </div>
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleClose}
              aria-label="Close"
            >
              <Close style={{ fontSize: 20 }} />
            </button>
          </div>

          {/* Upload Limit Error Display */}
          {currentLimitError && !isLoadingPhotos && (
            <div style={{
              margin: '8px 24px 16px 24px',
              padding: 16,
              backgroundColor: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: 8,
              color: '#DC2626'
            }}
            >
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: 600 }}>Upload Limit Exceeded</h4>
              <p style={{ margin: '0 0 8px 0', fontSize: 12, lineHeight: 1.4 }}>{currentLimitError.message}</p>
              <a
                href={userAccessedData ? '/affiliate' : Pathname.plans}
                style={{
                  color: '#5aa5ef',
                  fontSize: 12,
                  textDecoration: 'underline',
                  fontWeight: 500
                }}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(userAccessedData ? '/affiliate' : Pathname.plans);
                  handleClose();
                }}
              >
                Upgrade plan
              </a>
            </div>
          )}

          {/* Content */}
          <div style={{ overflowY: 'auto', padding: '0 24px' }}>
            {selectedPhotos.length === 0 && (!pendingPhotoFiles || pendingPhotoFiles.length === 0) ? (
              // File Selection Area
              (
<div className={styles.uploadArea}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpeg,.jpg,.png,.webp,.heic,.heif"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  className={styles.uploadPaper}
                >
                  <span className={styles.uploadIconWrap}>
                    <CloudUpload className={styles.uploadIcon} />
                  </span>
                  <h3 className={styles.uploadText}>
                    Choose photo files
                  </h3>
                  <p className={styles.subText}>
                    Click to browse, or drag and drop them here
                  </p>
                  <span className={styles.browseButton}>Browse files</span>
                  <p className={styles.uploadHint}>
                    JPEG, JPG, PNG, WebP, HEIC, HEIF
                  </p>
                </div>
</div>
)
            ) : selectedPhotos.length === 0 && pendingPhotoFiles && pendingPhotoFiles.length > 0 ? (
              // Loading state while processing pending files
              (
<div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: 400,
                flexDirection: 'column'
              }}
>
                <CircularProgress size={50} style={{ color: '#5aa5ef', marginBottom: 16 }} />
                <span style={{
                  fontSize: 16,
                  fontFamily: 'Montserrat',
                  color: '#6B7280'
                }}
                >
                  Loading photos...
                </span>
</div>
)
            ) : (
              // Photo Details Form
              (
<div style={{ paddingTop: 16 }}>
                {/* Album Type Radio Buttons */}
                <div style={{ marginBottom: 16, padding: '4px 0' }}>
                  <h3 style={{
                    color: '#374151',
                    fontFamily: 'Montserrat',
                    fontSize: '16px',
                    fontWeight: 600,
                    marginBottom: 8
                  }}
                  >
                    Album Options
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly' }}>
                    <FormControlLabel
                      control={(
                        <Radio
                          checked={albumMode === 0}
                          onChange={() => setAlbumMode(0)}
                          value={0}
                          style={{ color: '#5aa5ef' }}
                        />
                      )}
                      label={(
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <AddCircleOutline style={{ fontSize: 18, color: '#6B7280' }} />
                          <span style={{
                            fontFamily: 'Montserrat',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#374151'
                          }}
                          >
                            Create New Album
                          </span>
                        </div>
                      )}
                      style={{ margin: 0 }}
                    />
                    <FormControlLabel
                      control={(
                        <Radio
                          checked={albumMode === 1}
                          onChange={() => setAlbumMode(1)}
                          value={1}
                          style={{ color: '#5aa5ef' }}
                        />
                      )}
                      label={(
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FolderOpen style={{ fontSize: 18, color: '#6B7280' }} />
                          <span style={{
                            fontFamily: 'Montserrat',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#374151'
                          }}
                          >
                            Add to Existing Album
                          </span>
                        </div>
                      )}
                      style={{ margin: 0 }}
                    />
                  </div>
                </div>
                {/* Photo Grid */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <h3 className={styles.labelText}>
                      Selected Photos (
                      {selectedPhotos.length}
                      )
                    </h3>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button
                        onClick={handleAddMorePhotosClick}
                        disabled={currentLimitError || sameToAllowed}
                        style={{
                          color:
                            currentLimitError || sameToAllowed
                              ? '#9CA3AF'
                              : '#5aa5ef',
                          borderColor:
                            currentLimitError || sameToAllowed
                              ? '#D1D5DB'
                              : '#5aa5ef',
                          textTransform: 'none',
                          fontFamily: 'Montserrat',
                        }}
                        startIcon={<Add />}
                      >
                        Add More
                      </Button>
                      <Button
                        onClick={handleReorderModalOpen}
                        style={{
                          color: '#5aa5ef',
                          fontSize: 14,
                          fontFamily: 'Montserrat',
                          textTransform: 'none'
                        }}
                        startIcon={<PlaylistPlay />}
                      >
                        Reorder
                      </Button>
                    </div>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpeg,.jpg,.png,.webp,.heic,.heif"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />

                  <div
                    className={styles.photoGrid}
                    onDragOver={(e) => {
                      e.preventDefault();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                    }}
                  >
                    {selectedPhotos.map((photo, index) => (
                      <div
                        key={`${photo.name}-${photo.lastModified}`}
                        className={`${styles.photoItem} ${selectedThumbnailIndex === index ? styles.selectedPhoto : ''
                          }`}
                        onClick={() => setSelectedThumbnailIndex(index)}
                      >
                        <img
                          src={previewUrls[index] || photoUrls[index]}
                          alt={`Selected ${index + 1}`}
                          className={styles.photoImage}
                          loading="lazy"
                          decoding="async"
                        />

                        {/* Remove Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removePhoto(index);
                          }}
                          className={styles.removeButton}
                        >
                          <Delete style={{ fontSize: 16, color: '#fff' }} />
                        </button>

                        {/* Selected Indicator */}
                        {selectedThumbnailIndex === index && (
                          <div className={styles.selectedIndicator}>
                            <CheckCircle style={{ color: '#5aa5ef', fontSize: 20 }} />
                          </div>
                        )}

                        {/* Thumbnail Badge */}
                        {selectedThumbnailIndex === index && (
                          <div className={styles.thumbnailBadge}>
                            Thumbnail
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {/* Tab Panel Content */}
                {albumMode === 0 ? (
                  // Create Album Tab Content
                  (
<>
                    {/* Photo Title */}
                    <div style={{ marginBottom: 8 }}>
                      <h3 className={styles.labelText} style={{ marginBottom: 8 }}>
                        Title
                      </h3>
                      <TextField
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Enter photo album title"
                        variant="outlined"
                        fullWidth
                        className={styles.formField}
                      />
                    </div>
                    {/* Description and Visibility in same row */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 16,
                      marginBottom: 16
                    }}
                    >
                      {/* Description */}
                      <div>
                        <h3 className={styles.labelText} style={{ marginBottom: 8 }}>
                          Description
                        </h3>
                        <TextField
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Add a description for your photos"
                          variant="outlined"
                          fullWidth
                          className={styles.descriptionField}
                        />
                      </div>

                      {/* Visibility */}
                      <div>
                        <h3 className={styles.labelText} style={{ marginBottom: 8 }}>
                          Visibility
                        </h3>
                        <FormControl fullWidth variant="outlined">
                          <Select
                            value={visibility}
                            onChange={(e) => setVisibility(e.target.value)}
                            className={styles.selectField}
                          >
                            <MenuItem value="2">
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Public style={{ fontSize: 16, marginRight: 8, color: '#10B981' }} />
                                All Viewers
                              </div>
                            </MenuItem>
                            <MenuItem value="1">
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Lock style={{ fontSize: 16, marginRight: 8, color: '#EF4444' }} />
                                Private
                              </div>
                            </MenuItem>
                            <MenuItem value="3">
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Group style={{ fontSize: 16, marginRight: 8, color: '#8B5CF6' }} />
                                Group
                              </div>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </div>
                    </div>
                    {/* Group Selection (when visibility is Group) */}
                    {visibility === '3' && (
                      <div style={{ marginBottom: 16 }}>
                        <h3 className={styles.labelText} style={{ marginBottom: 8 }}>
                          Select Groups
                        </h3>
                        <div className={`${styles.groupSelection} ${(visibility === '3' && (!selectedGroups || selectedGroups.length === 0)) ? styles.groupSelectionWarning : ''
                          }`}
                        >
                          {!groupsLoaded ? (
                            <div className={styles.loadingContainer}>
                              <CircularProgress size={16} style={{ color: '#5aa5ef' }} />
                              <span className={styles.loadingText}>
                                Loading groups...
                              </span>
                            </div>
                          ) : groups.length === 0 ? (
                            <div className={`${styles.emptyGroupsContainer} ${visibility === '3' ? styles.emptyGroupsContainerWarning : ''
                              }`}
                            >
                              <span className={styles.emptyGroupsText}>
                                No groups available
                              </span>
                            </div>
                          ) : (
                            groups.map((group) => (
                              <FormControlLabel
                                key={group.id}
                                control={(
                                  <Checkbox
                                    checked={selectedGroups.includes(group.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedGroups([...selectedGroups, group.id]);
                                      } else {
                                        setSelectedGroups(selectedGroups.filter((id) => id !== group.id));
                                      }
                                    }}
                                    style={{ color: '#8B5CF6' }}
                                  />
                                )}
                                label={(
                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Group style={{ fontSize: 16, marginRight: 8, color: '#8B5CF6' }} />
                                    <span className={styles.groupText}>
                                      {group.title}
                                    </span>
                                  </div>
                                )}
                                className={styles.groupCheckbox}
                              />
                            ))
                          )}
                        </div>
                      </div>
                    )}
</>
)
                ) : (
                  // Existing Album Tab Content
                  (
<div style={{ marginBottom: 16 }}>
                    <h3 className={styles.labelText} style={{ marginBottom: 8 }}>
                      Select an Album
                    </h3>
                    {loadingAlbums ? (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          padding: '40px 0'
                        }}
                      >
                        <CircularProgress
                          size={40}
                          style={{ color: '#5aa5ef' }}
                        />
                      </div>
                    ) : existingAlbums.length === 0 ? (
                      <div
                        style={{
                          textAlign: 'center',
                          padding: '40px 20px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '12px',
                          border: '2px dashed #e5e7eb'
                        }}
                      >
                        <FolderOpen
                          style={{ fontSize: 48, color: '#9ca3af', marginBottom: 16 }}
                        />
                        <Typography
                          variant="h6"
                          style={{
                            color: '#6b7280',
                            fontFamily: 'Montserrat',
                            marginBottom: 8
                          }}
                        >
                          No Albums Found
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{
                            color: '#9ca3af',
                            fontFamily: 'Montserrat'
                          }}
                        >
                          Create a new album to get started
                        </Typography>
                      </div>
                    ) : (
                      <div
                        style={{
                          minHeight: '200px',
                          maxHeight: '400px',
                          overflowY: 'auto',
                          padding: '8px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '8px',
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                          gap: '12px',
                          alignContent: 'start'
                        }}
                      >
                        {existingAlbums.map((album) => (
                          <div
                            key={album.id}
                            style={{
                              cursor: 'pointer',
                              border: selectedAlbum?.id === album.id ? '2px solid #5aa5ef' : '2px solid #e5e7eb',
                              borderRadius: '10px',
                              transition: 'all 0.3s ease',
                              backgroundColor: selectedAlbum?.id === album.id ? '#f0f9ff' : '#fff',
                              boxShadow: selectedAlbum?.id === album.id
                                ? '0 4px 12px rgba(90, 165, 239, 0.2)'
                                : '0 2px 6px rgba(0,0,0,0.08)',
                              height: '90px',
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px',
                              width: '100%',
                              minWidth: '160px',
                              maxWidth: '200px'
                            }}
                            onClick={() => setSelectedAlbum(album)}
                          >
                            <div
                              style={{
                                width: '50px',
                                height: '50px',
                                flexShrink: 0,
                                borderRadius: '8px',
                                overflow: 'hidden',
                                backgroundColor: '#f3f4f6'
                              }}
                            >
                              <img
                                src={album.icon || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50"%3E%3Crect width="50" height="50" fill="%23e5e7eb"/%3E%3Cg fill="%239ca3af"%3E%3Cpath d="M25 16 L30 26 L20 26 Z"/%3E%3Cpath d="M18 20 L22 26 L14 26 Z"/%3E%3Ccircle cx="32" cy="16" r="2"/%3E%3C/g%3E%3C/svg%3E'}
                                alt={album.title}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            </div>
                            <div
                              style={{
                                flex: 1,
                                padding: '0 8px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                minWidth: 0,
                                position: 'relative'
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                style={{
                                  fontFamily: 'Montserrat',
                                  fontWeight: 600,
                                  fontSize: '13px',
                                  color: '#1f2937',
                                  marginBottom: 2,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  lineHeight: '18px'
                                }}
                              >
                                {album.title}
                              </Typography>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                {album.visibility && (
                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                    {album.visibility === '2' && (
                                      <Public style={{ fontSize: 12, color: '#10B981' }} />
                                    )}
                                    {album.visibility === '1' && (
                                      <Lock style={{ fontSize: 12, color: '#EF4444' }} />
                                    )}
                                    {album.visibility === '3' && (
                                      <Group style={{ fontSize: 12, color: '#8B5CF6' }} />
                                    )}
                                  </div>
                                )}
                                {selectedAlbum?.id === album.id && (
                                  <CheckCircle style={{ color: '#5aa5ef', fontSize: 16 }} />
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
</div>
)
                )}
</div>
)
            )}
          </div>

          {/* Footer Actions */}
          {selectedPhotos.length > 0 && (
            <div className={styles.footer}>
              <div className={styles.footerInfo}>
                <span className={styles.footerText}>
                  {selectedPhotos.length}
                  {' '}
                  photo
                  {selectedPhotos.length !== 1 ? 's' : ''}
                  {' '}
                  selected
                </span>
              </div>
              <div className={styles.footerActions}>
                <Button
                  onClick={handleClose}
                  className={styles.cancelButton}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePublish}
                  disabled={
                    currentLimitError
                    || (albumMode === 0 ? (
                      !title.trim()
                      || isPublishing
                      || (visibility === '3' && (!selectedGroups || selectedGroups.length === 0))
                    ) : (
                      !selectedAlbum || isPublishing
                    ))
                  }
                  variant="contained"
                  className={styles.publishButton}
                >
                  {isPublishing ? (
                    <div className={styles.uploadingContainer}>
                      <CircularProgress size={16} className={styles.uploadingText} />
                      {albumMode === 0 ? 'Publishing...' : 'Adding Photos...'}
                    </div>
                  ) : (
                    <div className={styles.uploadButtonContainer}>
                      <PhotoLibrary style={{ fontSize: 18 }} />
                      {albumMode === 0 ? 'Publish Album' : 'Add to Album'}
                    </div>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Reorder Modal */}
      <Dialog
        open={reorderModalOpen}
        onClose={() => setReorderModalOpen(false)}
        maxWidth={false}
        fullScreen
        PaperProps={{
          style: {
            backgroundColor: '#f8fafc',
          }
        }}
      >
        <DialogContent style={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100vh' }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            backgroundColor: '#fff',
            borderBottom: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <PlaylistPlay style={{ color: '#5aa5ef', fontSize: 24, marginRight: 12 }} />
              <h2 style={{
                color: '#374151',
                fontFamily: 'Montserrat',
                fontSize: '20px',
                fontWeight: 600,
                margin: 0
              }}
              >
                Reorder Photos
              </h2>
            </div>
            <IconButton
              onClick={() => setReorderModalOpen(false)}
              style={{ color: '#6B7280' }}
              size="large"
            >
              <Close />
            </IconButton>
          </div>

          {/* Content */}
          <div ref={reorderScrollRef} style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, maxWidth: '1200px', margin: '0 auto' }}>
              {selectedPhotos.map((photo, index) => {
                const isThumbnail = index === selectedThumbnailIndex;

                return (
                  <div
                    key={`${photo.name}-${photo.lastModified}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={handleReorderDragOver}
                    onDragEnter={handleReorderDragEnter}
                    onDrop={(e) => handleReorderDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    style={{
                      position: 'relative',
                      backgroundColor: '#fff',
                      borderRadius: 12,
                      overflow: 'hidden',
                      boxShadow: draggedIndex === index
                        ? '0 8px 24px rgba(0, 0, 0, 0.25)'
                        : '0 2px 8px rgba(0, 0, 0, 0.1)',
                      cursor: 'grab',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      transform: draggedIndex === index ? 'scale(1.05)' : 'scale(1)'
                    }}
                    onMouseEnter={(e) => {
                      if (draggedIndex !== index) {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (draggedIndex !== index) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                  >
                    <img
                      src={previewUrls[index] || photoUrls[index]}
                      alt={`${index + 1}`}
                      loading="lazy"
                      decoding="async"
                      style={{
                        width: '100%',
                        height: '150px',
                        objectFit: 'cover',
                        pointerEvents: 'none'
                      }}
                    />
                    <div style={{ padding: '12px', backgroundColor: '#fff' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 500, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {photo.name.length > 20 ? `${photo.name.substring(0, 20)}...` : photo.name}
                        </span>
                        <span style={{ backgroundColor: draggedIndex === index ? '#5aa5ef' : '#e5e7eb', color: draggedIndex === index ? '#fff' : '#6b7280', fontSize: '12px', fontFamily: 'Montserrat', fontWeight: 600, padding: '4px 8px', borderRadius: '12px' }}>
                          {index + 1}
                        </span>
                      </div>
                      {isThumbnail && (
                        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', color: '#5aa5ef', fontSize: '12px', fontFamily: 'Montserrat', fontWeight: 500 }}>
                          <CheckCircle style={{ fontSize: 16, marginRight: 4 }} />
                          Thumbnail
                        </div>
                      )}
                    </div>
                    {draggedIndex === index && (
                      <div style={{ position: 'absolute', top: 8, right: 8, backgroundColor: '#5aa5ef', color: '#fff', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold' }}>
                        ↕
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: '16px 24px', backgroundColor: '#fff', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={() => setReorderModalOpen(false)}
              variant="contained"
              style={{
                backgroundColor: '#5aa5ef',
                color: '#fff',
                fontFamily: 'Montserrat',
                textTransform: 'none',
                paddingLeft: '32px',
                paddingRight: '32px'
              }}
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Per-upload photo cap popup. Fires when a pick (or a drop) would push
          the selection past MAX_PHOTOS_PER_UPLOAD — the extras are dropped, so
          this is the only place the user learns about it. */}
      <Dialog
        open={Boolean(photoCapNotice)}
        onClose={() => setPhotoCapNotice(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: styles.capDialogPaper }}
      >
        <div className={styles.capDialog}>
          <span className={styles.capIconWrap}>
            <PhotoLibrary />
          </span>
          <h2 className={styles.capTitle}>Photo limit reached</h2>
          <p className={styles.capMessage}>{photoCapMessage}</p>
          <div className={styles.capActions}>
            <button
              type="button"
              className={styles.capButton}
              onClick={() => setPhotoCapNotice(null)}
            >
              Got it
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default PhotoUploadModal;
